import type { ChangeEvent } from 'react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, Company, Installation } from 'src/types';
import CheckboxSelect from 'src/UtilComponents/CheckboxSelect';

interface ProjectFormProps {
  initialData?: Project;
  companies: Company[];
  installations: Installation[];
  onSubmit: (data: Partial<Project>) => void;
  isLoading: boolean;
  onCancel: () => void;
}

const ProjectForm = ({
  initialData,
  companies,
  installations,
  onSubmit,
  isLoading,
  onCancel,
}: ProjectFormProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    address: initialData?.address ?? '',
    company_id: initialData?.company_id ?? '',
  });
  const [selectedInstallations, setSelectedInstallations] = useState<string[]>(
    initialData?.installation_ids ?? []
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const data: Partial<Project> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value !== '') {
        (data as Record<string, string>)[key] = value;
      }
    }
    data.installation_ids = selectedInstallations;
    onSubmit(data);
  };

  return (
    <form className="management-form" onSubmit={handleSubmit}>
      <div className="management-form-field">
        <label htmlFor="project-name">{t('management.project.name')} *</label>
        <input
          id="project-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="project-description">{t('management.project.description')}</label>
        <textarea
          id="project-description"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="project-address">{t('management.project.address')}</label>
        <input
          id="project-address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="project-company">{t('management.project.company')}</label>
        <select
          id="project-company"
          name="company_id"
          value={formData.company_id}
          onChange={handleChange}
        >
          <option value="">{t('management.project.selectCompany')}</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="management-form-field">
        <label>{t('management.project.installations')}</label>
        <CheckboxSelect
          options={installations.map((i) => ({ id: i.id, name: i.name }))}
          selectedOptions={selectedInstallations}
          onChange={setSelectedInstallations}
          isLoading={false}
        />
      </div>
      <div className="entity-modal-footer" style={{ padding: 0, border: 'none' }}>
        <button
          type="button"
          className="management-btn management-btn--secondary"
          onClick={onCancel}
        >
          {t('management.cancel')}
        </button>
        <button
          type="submit"
          className="management-btn management-btn--primary"
          disabled={isLoading}
        >
          {isLoading ? t('management.saving') : t('management.save')}
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;
